import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faRedo, faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { startCase } from "lodash";
import moment from "moment";
import { fetchQuery } from "relay-runtime";

import AppContext from "~/common/AppContext";
import { promptGoogleAuth, promptOffice365Auth } from "~/lib/auth/index";
import { inCalendarMode } from "~/lib/utils";
import { environment } from "~/models";
import OngoingMeetingContext from "~/OngoingMeetingContext";
import toast, { ToastActionButton } from "~/ui/toast";

import type { ResyncCalendarAsyncTaskQuery } from "~/__generated__/ResyncCalendarAsyncTaskQuery.graphql";
import type { ResyncCalendarMutation as ResyncCalendarMutationType } from "~/__generated__/ResyncCalendarMutation.graphql";

import styles from "~/nav/panels/Meetings/ResyncCalendar.module.css";

const SERVICE_TO_PROMPT = {
	google: {
		prompt: promptGoogleAuth,
		code: "access_denied",
	},
	office365: {
		prompt: promptOffice365Auth,
		code: "consent_required",
	},
};

const ResyncCalendarMutation = graphql`
	mutation ResyncCalendarMutation($currentDate: String) {
		resyncCalendar(currentDate: $currentDate) {
			ok
			error {
				__typename
				... on ReauthError {
					service
				}
				message
			}
			asyncResultId
		}
	}
`;

const AsyncTaskQuery = graphql`
	query ResyncCalendarAsyncTaskQuery($taskId: JSON!) {
		viewer {
			asyncTask(taskId: $taskId, ignoreChildren: true) {
				taskId
				state
				info
			}
		}
	}
`;

interface Props {
	resyncRef?: { onResyncClicked?: () => void };
	startSync?: () => void;
	afterSync: () => void;
	currentDate: Date;
	lastSynced: string;
	calendarServiceToReconnect: string | null;
	makeWayForReturnToMeetingPrompt?: boolean;
}

export default function ResyncCalendar({
	resyncRef,
	lastSynced,
	startSync,
	afterSync,
	currentDate,
	makeWayForReturnToMeetingPrompt,
}: Props) {
	const { openSettingsModal } = React.useContext(AppContext);
	const openCalendarSettings = React.useCallback(() => openSettingsModal("preferences"), [openSettingsModal]);

	const [taskId, setTaskId] = React.useState<string | null>(null);
	const [hovering, setHovering] = React.useState(false);
	const [pending, setPending] = React.useState(false);
	const [clicked, setClicked] = React.useState(false);

	const [tries, setTries] = React.useState(0);
	const [recentlySynced, setRecentlySynced] = React.useState(false);

	const showErrorToast = React.useCallback(
		(title?: React.ReactNode, content?: React.ReactNode) => {
			return toast.error(content || "There was an issue syncing your calendar(s)", {
				title: title || "Check the calendar status in settings",
				actions: (
					<>
						<ToastActionButton onClick={openCalendarSettings}>Open settings</ToastActionButton>
					</>
				),
			});
		},
		[openCalendarSettings],
	);

	const refreshState = React.useCallback(() => {
		if (!taskId) return;

		startSync?.();
		fetchQuery<ResyncCalendarAsyncTaskQuery>(environment, AsyncTaskQuery, { taskId })
			.toPromise()
			.then(resp => {
				if (!resp?.viewer.asyncTask) return;

				const { state, info } = resp.viewer.asyncTask;
				if (state == "SUCCESS") {
					setPending(false);
					setRecentlySynced(true);
					setTries(0);

					setTimeout(() => setRecentlySynced(false), 5000);
					toast.success("Calendar Synced.");
					afterSync?.();
				} else if (state == "PENDING") {
					setTries(prevTries => prevTries + 1);
				} else {
					setPending(false);
					if (info.error) {
						const { type, errorType, message } = info.error;
						if (errorType == "ReauthError") {
							showErrorToast();
						} else if (type == "NotAcquired") {
							if (clicked) {
								setClicked(false);
								showErrorToast("A sync is already in progress", "Try again in a little bit");
							} else {
								showErrorToast(startCase(type ?? errorType), message);
							}
						} else {
							showErrorToast();
						}
					}
				}
			});
	}, [afterSync, clicked, showErrorToast, startSync, taskId]);

	React.useEffect(() => {
		if (tries === 0) return;

		const timeout = window.setTimeout(refreshState, Math.min(2 ** tries * 1000, 60 * 1000));

		return () => clearTimeout(timeout);
	}, [tries, refreshState]);

	const onResyncClicked = React.useCallback(() => {
		if (pending) return;

		setPending(true);
		setClicked(true);

		commitMutation<ResyncCalendarMutationType>(environment, {
			mutation: ResyncCalendarMutation,
			variables: {
				currentDate: currentDate.toISOString(),
			},
			onCompleted: ({ resyncCalendar }: ResyncCalendarMutationType["response"]) => {
				if (!resyncCalendar) return;

				const { ok, error, asyncResultId } = resyncCalendar;
				if (ok && asyncResultId) {
					setTaskId(asyncResultId);
				}
				if (!ok) {
					setPending(false);

					if (!error) return;

					const { __typename: type, service, message } = error;
					if (type == "ReauthError") {
						toast.warn(
							<div>
								<strong>{startCase(type)}</strong>
								<div>{message}</div>
							</div>,
							{
								closeButton: false,
								autoClose: false,
								onClick: () => {
									const serviceAsKey = service as keyof typeof SERVICE_TO_PROMPT;
									SERVICE_TO_PROMPT[serviceAsKey]
										.prompt({ refresh: true })
										.then(onResyncClicked)
										.catch(({ code, message: oauthMessage }) => {
											if (code == SERVICE_TO_PROMPT[serviceAsKey].code) {
												toast.error(`Fellow requires your calendar (${oauthMessage})`);
											} else {
												toast.error(oauthMessage);
											}
										});
								},
							},
						);
					} else if (type == "NoCalendarProviderError") {
						if (inCalendarMode) {
							toast.warn(
								<div>
									<strong>{startCase(type)}</strong>
									<div>{message}</div>
								</div>,
								{
									closeButton: false,
									autoClose: false,
									onClick: () => {
										openSettingsModal("apps-and-integrations");
									},
								},
							);
						} else {
							toast.warn(
								<div>
									<strong>{startCase(type)}</strong>
									<div>{message}</div>
								</div>,
								{
									closeButton: false,
									autoClose: false,
									onClick: () => {
										openSettingsModal("apps-and-integrations");
									},
								},
							);
						}
					} else {
						showErrorToast();
					}
				}
			},
		});
	}, [currentDate, openSettingsModal, pending, showErrorToast]);

	React.useEffect(() => {
		if (!resyncRef) return;

		resyncRef.onResyncClicked = onResyncClicked;
	}, [resyncRef, onResyncClicked]);

	React.useEffect(() => {
		if (!taskId) return;
		const timeout = window.setTimeout(refreshState, 2000);

		return () => clearTimeout(timeout);
	}, [taskId, refreshState]);

	const onMouseEnter = React.useCallback(() => setHovering(true), []);
	const onMouseLeave = React.useCallback(() => setHovering(false), []);

	const { ongoingMeeting } = React.useContext(OngoingMeetingContext);

	const greyText = pending || recentlySynced;
	const hasText = (lastSynced && hovering && !pending) || recentlySynced;

	return (
		<button
			title="Sync calendar now"
			className={cx(styles.resyncButton, {
				[styles.shiftedUp]: Boolean(ongoingMeeting && makeWayForReturnToMeetingPrompt),
				[styles.greyText]: greyText,
				[styles.hasText]: hasText,
			})}
			onClick={onResyncClicked}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<FontAwesomeIcon className={styles.icon} icon={pending ? faSpinnerThird : faRedo} spin={pending} />
			{lastSynced && (
				<span className={styles.wrapper}>
					<span className={styles.text}>Synced {moment.utc(lastSynced).local().format("h:mm A")}</span>
				</span>
			)}
		</button>
	);
}
